
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Cargo } from '@/models/Cargo'
import { Transport } from '@/models/Transport'
import TransportType from '@/enums/TransportType'
import { Company } from '@/models/Company'
import { Terminal } from '@/models/Terminal'
import DateTimePicker from '@/components/DateTimePicker.vue'
import { CargoType } from '@/models/CargoType'
import { BillOfLadingTransport } from '@/models/BillOfLadingTransport'
import Notification from '@/classes/Notification'
import i18n from '@/i18n'
import { Model } from '@/models/Model'
import GeoDirection from '@/directions/types/GeoDirection'
import FacilityDirection from '@/directions/types/FacilityDirection'
import { GeoJSON } from 'geojson'
import LocationSelector from '@/components/LocationSelector.vue'
import { namespace } from 'vuex-class'
import CargoSizeQuantityUnit from '@/enums/CargoSizeQuantityUnit'
import DOMPurify from 'dompurify'

const Auth = namespace('auth')
const TransportDetails = namespace('transportDetails')

@Component({
	components: {
		DateTimePicker,
		LocationSelector
	}
})
export default class TransportDocumentGenerator extends Vue {
	@Prop({ required: true })
	transport!: Transport

	@Auth.State('user')
	user!: { id }

	@Auth.Getter('currentCompany')
	carrier!: Company

	@TransportDetails.State('cargo')
	cargo!: Cargo | null

	@TransportDetails.Action('fetchCargoLazy')
	fetchCargo

	transportType: TransportType = TransportType.BULK

	isLoading: boolean = false
	isDocumentGenerating: boolean = false
	cargoTypesLoading: boolean = false

	cargoTypes: Array<CargoType> = []
	shipper: Company | null = null
	vesselCompany: Company | null = null

	loadingPlace: GeoDirection | FacilityDirection | null = null
	unloadingPlace: GeoDirection | FacilityDirection | null = null
	startTerminal: Terminal | null = null
	destinationTerminal: Terminal | null = null

	companyName: string = ''
	product: { value; text } | string = ''
	issueLocation: string = ''
	quantity: string = ''
	quantityUnit: 'tonnes' | 'cubic_meters' = 'tonnes'
	declarationShipper: string = ''
	instructionsShipper: string = ''
	remark: string = ''

	openStartTerminalDetails = false
	openStartLocationCreator = false
	openDestinationTerminalDetails = false
	openDestinationLocationCreator = false
	terminalCreatorEditMode = false

	locales = ['nl', 'en', 'de', 'fr']
	selectedLanguage: string | null = null

	get vesselExcerpt() {
		const vessel = this.transport?.getActiveVessel()
		return `${vessel?.name} (${vessel?.euNumber})`
	}

	get vesselData() {
		return this.transport?.getActiveVessel()
	}

	get filteredCargoTypes(): Array<CargoType> {
		return this.cargoTypes.filter((cargoType) => cargoType.bulkType !== 'liquid_bulk')
	}

	async created() {
		this.isLoading = true

		try {
			await this.fetchCargo()
			const cargoTypesQuery = CargoType.fetchAllLazy()

			this.companyName = this.carrier!.name ?? ''

			const vessel = this.transport.getActiveVessel()
			const vesselCompanyQuery = vessel.company().get()

			let startTerminalQuery = Promise.resolve(null as any)
			if (this.cargo!.getStartTerminal()) {
				startTerminalQuery = Terminal.fetchLazy(this.cargo!.getStartTerminal().getApiId() as string)
			}
			let destinationTerminalQuery = Promise.resolve(null as any)
			if (this.cargo!.getDestinationTerminal()) {
				destinationTerminalQuery = Terminal.fetchLazy(this.cargo!.getDestinationTerminal().getApiId() as string)
			}

			this.cargoTypes = await cargoTypesQuery

			this.shipper = this.cargo!.getClient() || this.carrier
			this.vesselCompany = (await vesselCompanyQuery).getData() as Company

			if (this.cargo) {
				this.transportType = this.cargo.transport
			}

			const cargoType = this.cargo!.getCargoType() || null
			if (cargoType) {
				this.product = {
					text: cargoType.title,
					value: cargoType.getApiId()
				}
			}
			if (this.cargo!.customProduct) {
				this.product = this.cargo!.customProduct
			}

			this.initLoadingPlace(await startTerminalQuery)
			this.initUnloadingPlace(await destinationTerminalQuery)

			const loadingLocation = this.transport.getLocations()[0]
			if (loadingLocation.loadedQuantity) {
				this.quantity = loadingLocation.loadedQuantity.toString()
			}
			this.quantityUnit = this.cargo?.cargoSizeUnit === CargoSizeQuantityUnit.PER_TON ? 'tonnes' : 'cubic_meters'
			this.issueLocation = this.carrier?.place || ''
			this.selectedLanguage = this.$i18n.locale
		} catch (e) {
			// eslint-disable-next-line
			console.error('Error occurred while fetching data for bill-of-lading', e)
			await Notification.Error(i18n.t('documentGenerator.errorWhileLoading').toString())
			this.closeDocumentCreator()
		} finally {
			this.isLoading = false
		}
	}

	initLoadingPlace(terminal) {
		if (this.cargo!.getStartTerminal()) {
			this.loadingPlace = FacilityDirection.fromTerminal(terminal)
		} else {
			const direction = new GeoDirection()
			direction.geo = this.cargo!.customStartLocationGeo as GeoJSON.Point
			direction.country = this.cargo!.customStartLocationCountryCode || ''
			direction.text = this.cargo!.customStartLocationPlace || ''

			this.loadingPlace = direction
		}
	}

	initUnloadingPlace(terminal) {
		if (this.cargo!.getDestinationTerminal()) {
			this.unloadingPlace = FacilityDirection.fromTerminal(terminal)
		} else {
			const direction = new GeoDirection()
			direction.geo = this.cargo!.customDestinationGeo as GeoJSON.Point
			direction.country = this.cargo!.customDestinationCountryCode || ''
			direction.text = this.cargo!.customDestinationPlace || ''

			this.unloadingPlace = direction
		}
	}

	closeDocumentCreator() {
		this.$emit('creator-closed')
	}

	@Watch('loadingPlace')
	async updateStartTerminal(newVal) {
		if (newVal && newVal instanceof FacilityDirection && newVal.facilityType === 'terminals') {
			this.startTerminal = (await Terminal.find(newVal.facilityId)).getData() as Terminal
		} else {
			this.startTerminal = null
		}
	}

	@Watch('unloadingPlace')
	async updateDestinationTerminal(newVal) {
		if (newVal && newVal instanceof FacilityDirection && newVal.facilityType === 'terminals') {
			this.destinationTerminal = (await Terminal.find(newVal.facilityId)).getData() as Terminal
		} else {
			this.destinationTerminal = null
		}
	}

	openStartTerminalCreatorForCreate() {
		this.terminalCreatorEditMode = false
		this.openStartLocationCreator = true
	}

	openStartTerminalCreatorForEdit() {
		this.openStartTerminalDetails = false
		this.terminalCreatorEditMode = true
		this.openStartLocationCreator = true
	}

	openDestinationTerminalCreatorForCreate() {
		this.terminalCreatorEditMode = false
		this.openDestinationLocationCreator = true
	}

	openDestinationTerminalCreatorForEdit() {
		this.openDestinationTerminalDetails = false
		this.terminalCreatorEditMode = true
		this.openDestinationLocationCreator = true
	}

	async setNewTerminalAsLoadingPlace(terminal) {
		const direction = new FacilityDirection()
		direction.facilityId = terminal.getApiId() as string
		direction.facilityType = 'terminals'
		direction.text = terminal.getName()

		this.loadingPlace = direction
		this.openStartLocationCreator = false
	}

	async setNewTerminalAsUnloadingPlace(terminal) {
		const direction = new FacilityDirection()
		direction.facilityId = terminal.getApiId() as string
		direction.facilityType = 'terminals'
		direction.text = terminal.getName()

		this.unloadingPlace = direction
		this.openDestinationLocationCreator = false
	}

	async generateDocument() {
		const valid = await this.$validator.validateAll()
		if (!valid) return

		this.isDocumentGenerating = true

		try {
			const bill = await this.prepareBillOfLading()

			await bill.doCallbackWithLanguage(() => {
				return bill.save()
			}, this.selectedLanguage)

			this.closeDocumentCreator()
		} finally {
			this.isDocumentGenerating = false
		}
	}

	async prepareBillOfLading(): Promise<BillOfLadingTransport> {
		const bill = new BillOfLadingTransport()
		bill.setShipper(this.shipper!)
		bill.setCarrier(this.carrier!)
		bill.companyName = this.companyName
		bill.issueLocation = this.issueLocation
		bill.declarationShipper = this.declarationShipper
		bill.instructionsShipper = this.instructionsShipper
		bill.remark = DOMPurify.sanitize(this.remark)
		bill.quantity = this.quantity
		bill.quantityUnit = this.quantityUnit

		bill.setTransport(this.transport)
		if (this.transport.getVessel()) {
			bill.setVessel(this.transport.getVessel())
		} else {
			bill.setUnregisteredVessel(this.transport.getUnregisteredVessel())
		}

		await this.prepareStartLocation(bill)
		await this.prepareDestinationLocation(bill)

		if (typeof this.product === 'object') {
			bill.setCargoType(await CargoType.fetchLazy(this.product.value))
		} else {
			bill.customProduct = this.product
		}
		return bill
	}

	async prepareStartLocation(bill) {
		// reset and set start location fields
		bill.setStartTerminal(null)
		bill.customStartAddress = null
		bill.customStartCountryCode = null
		bill.customStartGeometry = null
		bill.setRelation('startTerminal', null)
		// Check if the user specified a custom start terminal or not...
		if (this.loadingPlace instanceof FacilityDirection) {
			if (this.loadingPlace.facilityType === 'terminals') {
				const startTerminal = await Terminal.fetchLazy(this.loadingPlace.facilityId as string)
				bill.setStartTerminal(startTerminal)
			}
		} else if (this.loadingPlace instanceof GeoDirection) {
			bill.customStartAddress = this.loadingPlace.text
			bill.customStartCountryCode = this.loadingPlace.country

			// @ts-ignore
			if (typeof this.loadingPlace.geo === 'string') {
				bill.customStartGeometry = this.loadingPlace.geo
			} else {
				bill.customStartGeometry = `POINT(${this.loadingPlace.geo.coordinates[0]} ${this.loadingPlace.geo.coordinates[1]})`
			}
		}
	}

	async prepareDestinationLocation(bill) {
		// reset and set destination location fields
		bill.setDestinationTerminal(null)
		bill.customDestinationAddress = null
		bill.customDestinationCountryCode = null
		bill.customDestinationGeometry = null
		bill.setRelation('destinationTerminal', null)
		if (this.unloadingPlace instanceof FacilityDirection) {
			if (this.unloadingPlace.facilityType === 'terminals') {
				const destinationTerminal = await Terminal.fetchLazy(this.unloadingPlace.facilityId as string)
				bill.setDestinationTerminal(destinationTerminal)
			}
		} else if (this.unloadingPlace instanceof GeoDirection) {
			bill.customDestinationAddress = this.unloadingPlace.text
			bill.customDestinationCountryCode = this.unloadingPlace.country

			// @ts-ignore
			if (typeof this.unloadingPlace.geo === 'string') {
				bill.customDestinationGeometry = this.unloadingPlace.geo
			} else {
				bill.customDestinationGeometry = `POINT(${this.unloadingPlace.geo.coordinates[0]} ${this.unloadingPlace.geo.coordinates[1]})`
			}
		}
	}

	private mapItemsForCombobox(
		items: Array<Model>,
		attribute: string | any = 'title'
	): Array<{ value: string; text: string }> {
		return items.map((item) => {
			let text = ''
			if (typeof attribute === 'string') {
				text = item[attribute]
			} else {
				text = attribute(item)
			}

			return {
				value: item.getApiId() as string,
				text
			}
		})
	}

	get isLiquid(): boolean {
		return this.transportType === TransportType.LIQUID
	}
}
